import CustomDialog from 'Components/CustomDialog'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import { useGetCatalogsQuery } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import { useDeleteSingleProductMutation } from 'Services/ProductManagement/Features/Product/productApi'
import { type Product, ProductDeleteModes } from 'Services/ProductManagement/Features/Product/types'
import { useDeleteTariffProduct } from 'Services/ProductManagement/Features/Tariff/Hooks'
import type { ProductTariffError } from 'Util/ApiClient'
import { IS_HSL_SERVICE_ENVIRONMENT, IS_WALTTI_SERVICE_ENVIRONMENT } from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material'
import { Box } from '@mui/system'
import { useEffect } from 'react'
import { t } from 'ttag'

interface TariffProductDeleteDialogProps {
  open: boolean
  handleClose: () => void
  singleProduct: Product
  tariffId: string | undefined
  onDeleteSuccess: () => void
}

export default function TariffProductDeleteDialog({
  open,
  handleClose,
  singleProduct,
  tariffId,
  onDeleteSuccess
}: Readonly<TariffProductDeleteDialogProps>) {
  const {
    data: { resources: tariffsCatalogs } = { resources: [] },
    isFetching: isTariffsCatalogsFetching,
    error
  } = useGetCatalogsQuery(
    {
      bypass_pagination: true,
      tariff_id: tariffId,
      populate_product_codes: true
    },
    {
      skip: !open || !tariffId || IS_HSL_SERVICE_ENVIRONMENT,
      refetchOnMountOrArgChange: true
    }
  )

  const [deleteProduct, { isLoading: isDeleteProductLoading }] = useDeleteSingleProductMutation()
  const [deleteTariffProduct, { isLoading: isDeleteTariffProductLoading }] =
    useDeleteTariffProduct()

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress handleClose
  useEffect(() => {
    if (error && open) {
      handleClose()
      SnackbarUtils.error(
        t`An unexpected error occurred. Product deletion not possible at the moment.`
      )
    }
  }, [error, open])

  const catalogsWithProductCopy = tariffsCatalogs.filter(catalog =>
    catalog.product_codes?.includes(singleProduct.product_code)
  )
  const hasProductCopyInCatalog = catalogsWithProductCopy.length > 0
  const hasOrphanedCatalogs = singleProduct.catalog_ids.length > 0

  const title = hasProductCopyInCatalog ? t`Cannot delete product` : t`Are you sure?`

  const getConfirmMessage = () => {
    if (hasProductCopyInCatalog) {
      return t`This product has copies in catalogs. Remove all copies from catalogs before deleting the product.`
    }
    if (hasOrphanedCatalogs) {
      return t`This product has not been removed from all catalogs, are you sure you want to delete the product?`
    }
    return t`Are you sure you want to delete the product?`
  }

  const confirmMessage = getConfirmMessage()

  const handleTariffProductDelete = (productId: string) => {
    deleteProduct({ id: productId, mode: ProductDeleteModes.CASCADE })
      .unwrap()
      .then(() => {
        onDeleteSuccess()
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(({ message }: ProductTariffError) => {
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error deleting the product.`)
        }
      })
  }

  const handleWalttiTariffProductDelete = (productId: string) => {
    if (!tariffId) return

    deleteTariffProduct({ tariffId, productId })
      .unwrap()
      .then(() => {
        onDeleteSuccess()
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { message } = errorResponse
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error deleting the product.`)
        }
      })
  }

  const handleProductDelete = IS_WALTTI_SERVICE_ENVIRONMENT
    ? handleWalttiTariffProductDelete
    : handleTariffProductDelete

  return (
    <CustomDialog open={open} handleClose={handleClose}>
      {isTariffsCatalogsFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent sx={{ minHeight: 80 }}>
            <DialogContentText color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
              <Typography>{confirmMessage}</Typography>
              {hasProductCopyInCatalog && (
                <>
                  <Typography sx={{ mt: 1 }} fontWeight="bold">
                    {t`Product's sales channels`}:
                  </Typography>
                  {catalogsWithProductCopy.map(catalog => (
                    <Typography key={catalog.id}>{catalog.name}</Typography>
                  ))}
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button sx={{ color: 'text.primary' }} onClick={handleClose}>
              {hasProductCopyInCatalog ? t`Close` : t`No`}
            </Button>
            {!hasProductCopyInCatalog && (
              <CustomLoadingButton
                onClick={() => handleProductDelete(singleProduct.id)}
                isLoading={isDeleteProductLoading || isDeleteTariffProductLoading}
              >{t`Yes`}</CustomLoadingButton>
            )}
          </DialogActions>
        </>
      )}
    </CustomDialog>
  )
}
